import type {App, Component} from 'vue'
import {createVNode, render} from 'vue'

declare module '#app' {
    interface NuxtApp {
        $open: <T = any>(component: Component, props?: Record<string, any>, slot?: unknown) => Promise<T>
        $closeDialog: <T = any>() => Promise<T>
    }
}

declare module 'vue' {
    interface ComponentCustomProperties {
        $open: <T = any>(component: Component, props?: Record<string, any>, slot?: unknown) => Promise<T>
        $closeDialog: <T = any>() => Promise<T>
    }
}

const ROOT_ID = 'app-dialog-root'

type Entry = { container: HTMLElement }

// Stack of currently mounted dialogs/toasts, oldest first. Each entry owns its own
// dedicated DOM node so overlapping calls (e.g. a toast fired right before a route
// navigation, or several toasts in a row) never clobber one another.
const activeEntries: Entry[] = []

// The root is appended directly to <body>, outside of the Nuxt page/router tree, so it
// is never removed or replaced when the route changes (unlike the previous approach of
// querying `.v-sheet`/`.v-application__wrap`, which could match a different, or no
// longer existing, element after a navigation and silently drop the dialog/toast).
function ensureRoot(): HTMLElement {
    let root = document.getElementById(ROOT_ID)
    if (!root) {
        root = document.createElement('div')
        root.id = ROOT_ID
        document.body.appendChild(root)
    }
    return root
}

function unmount(entry: Entry) {
    render(null, entry.container)
    entry.container.remove()
    const index = activeEntries.indexOf(entry)
    if (index !== -1) activeEntries.splice(index, 1)
}

export default defineNuxtPlugin(({vueApp, provide}) => {
    const $open = (app: App<Element>) => (component: Component, props?: Record<string, any>, slot?: unknown) => {
        return new Promise((resolve) => {
            const container = document.createElement('div')
            ensureRoot().appendChild(container)
            const entry: Entry = {container}
            activeEntries.push(entry)

            const vNode = createVNode(component, {
                ...props,
                close: (returnValue: any) => {
                    unmount(entry)
                    resolve(returnValue)
                },
            }, slot)
            vNode.appContext = app._context
            render(vNode, container)
        })
    }

    // Closes the most recently opened dialog/toast without resolving its promise,
    // matching the previous behaviour (used as a "just in case there is nothing open,
    // clean up" call).
    const closeDialog = () => {
        const last = activeEntries[activeEntries.length - 1]
        if (last) unmount(last)
    }

    provide('open', $open(vueApp))
    provide('closeDialog', closeDialog)
})